import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { REGISTRATION_FEES, BANQUET_TICKET_PRICE } from '../../mock';
import { amountReceived } from './EditPaymentModal';

const inputCls = 'w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none';
const priceOf = (cat) => REGISTRATION_FEES.find((f) => f.category === cat)?.price || 0;

function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] tracking-widest text-gold font-heading mb-1">{label}</span>{children}</label>;
}

const startItems = (r) => {
  if (r.items?.length) return r.items;
  const base = priceOf(r.category);
  const items = [{ type: 'registration', label: r.category, quantity: 1, unit_price: base, amount: base }];
  const extra = Number(r.fee || 0) - base;
  if (extra > 0) items.push({ type: 'late_fee', label: 'Late Registration Fee', quantity: 1, unit_price: extra, amount: extra });
  return items;
};

export const buildItems = (items, category, banquetQty) => {
  const base = priceOf(category);
  const out = items.filter((i) => i.type !== 'registration' && i.type !== 'banquet');
  if (items.some((i) => i.type === 'registration')) out.unshift({ type: 'registration', label: category, quantity: 1, unit_price: base, amount: base });
  if (banquetQty > 0) out.push({ type: 'banquet', label: 'Banquet Ticket', quantity: banquetQty, unit_price: BANQUET_TICKET_PRICE, amount: banquetQty * BANQUET_TICKET_PRICE });
  return out;
};

export const EditDetailsModal = ({ record, onClose, onSave, saving }) => {
  const items0 = startItems(record);
  const [f, setF] = useState({
    name: record.name || '', email: record.email || '', phone: record.phone || '',
    chapter: record.chapter || '', union: record.union || '', category: record.category || REGISTRATION_FEES[0].category,
    banquetQty: items0.find((i) => i.type === 'banquet')?.quantity || 0,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const items = buildItems(items0, f.category, Number(f.banquetQty) || 0);
  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const fee = items.filter((i) => i.type === 'registration' || i.type === 'late_fee').reduce((s, i) => s + Number(i.amount || 0), 0);
  const cats = REGISTRATION_FEES.some((c) => c.category === f.category) ? REGISTRATION_FEES.map((c) => c.category) : [f.category, ...REGISTRATION_FEES.map((c) => c.category)];

  const received = amountReceived(record);
  const status = record.payment_status === 'Refunded' ? 'Refunded' : (total > 0 && received >= total ? 'Paid' : received > 0 ? 'Partial' : 'Pending');
  const save = () => onSave({ name: f.name.trim(), email: f.email.trim(), phone: f.phone, chapter: f.chapter, union: f.union, category: f.category, items, fee, total_paid: total, amount_received: received, payment_status: status });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" data-testid="edit-details-modal">
      <div className="w-full max-w-lg card-dark rounded-md border border-gold/40 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold/30 bg-cbtu-green/40">
          <h3 className="font-heading tracking-widest text-gold text-sm">EDIT REGISTRATION</h3>
          <button onClick={onClose} className="text-cbtu-muted hover:text-gold" aria-label="Close" data-testid="edit-details-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="NAME"><input data-testid="edit-name" value={f.name} onChange={set('name')} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="EMAIL"><input data-testid="edit-email" type="email" value={f.email} onChange={set('email')} className={inputCls} /></Field>
            <Field label="PHONE"><input data-testid="edit-phone" value={f.phone} onChange={set('phone')} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CHAPTER"><input data-testid="edit-chapter" value={f.chapter} onChange={set('chapter')} className={inputCls} /></Field>
            <Field label="UNION"><input data-testid="edit-union" value={f.union} onChange={set('union')} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="REGISTRATION TYPE">
              <select data-testid="edit-category" value={f.category} onChange={set('category')} className={inputCls}>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={`BANQUET TICKETS ($${BANQUET_TICKET_PRICE} EA)`}><input data-testid="edit-banquet-qty" type="number" min="0" max="50" value={f.banquetQty} onChange={set('banquetQty')} className={inputCls} /></Field>
          </div>
          <div className="rounded-sm border border-gold/20 bg-cbtu-black/60 px-4 py-3 text-sm text-cbtu-cream" data-testid="edit-details-items">
            {items.map((i, n) => <div key={n} className="flex justify-between"><span>{i.label}{i.quantity > 1 ? ` × ${i.quantity}` : ''}</span><span>${i.amount}</span></div>)}
            <div className="flex justify-between border-t border-gold/20 mt-2 pt-2 font-heading tracking-widest text-gold"><span>NEW TOTAL DUE</span><span data-testid="edit-details-total">${total}</span></div>
            <div className="text-[11px] text-cbtu-muted mt-1">Received so far: ${received} · Balance: ${total - received} · Status will be {status}</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gold/30 bg-cbtu-black/60">
          <button onClick={onClose} className="ghost-button px-4 py-2 rounded-sm text-xs tracking-widest font-heading">CANCEL</button>
          <button onClick={save} disabled={saving || !f.name.trim() || !f.email.trim()} data-testid="edit-details-save" className="gold-button px-5 py-2 rounded-sm text-xs inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={13} /> {saving ? 'SAVING…' : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    </div>
  );
};
